/**
 * Extracts the user-facing error message from an Amplify API error.
 *
 * Amplify v6 REST throws a RestApiError for non-2xx responses. The error
 * has a `response` property with `{ statusCode, headers, body }` where
 * `body` is the raw response text (string). For non-AWS errors, the error
 * name/message default to "UnknownError"/"Unknown error".
 *
 * This function extracts the backend's `{ error: "..." }` JSON from the
 * response body string.
 */
export async function extractApiError(err: unknown): Promise<string> {
  const errObj = err as Record<string, unknown>;

  // Amplify v6 RestApiError: response.body is a string (already consumed)
  const response = errObj.response as
    | { body?: string; statusCode?: number }
    | undefined;
  if (response && typeof response.body === "string") {
    try {
      const parsed = JSON.parse(response.body) as { error?: string };
      if (typeof parsed.error === "string" && parsed.error !== "") {
        return parsed.error;
      }
    } catch {
      // not JSON — fall through
    }
  }

  // Fallback: response.body might be an object with .json() (older Amplify versions)
  const bodyObj = response?.body as
    | { json?: () => Promise<unknown> }
    | undefined;
  if (bodyObj && typeof bodyObj.json === "function") {
    try {
      const parsed = (await bodyObj.json()) as { error?: string };
      if (typeof parsed.error === "string" && parsed.error !== "") {
        return parsed.error;
      }
    } catch {
      // body already consumed or not JSON — fall through
    }
  }

  // Network / fetch failures
  if (err instanceof TypeError && /failed to fetch/i.test(err.message)) {
    return "Unable to reach the server. Check your network connection and try again.";
  }

  // If the Error has a meaningful message (not "Unknown error"), use it
  if (
    err instanceof Error &&
    err.message.trim() !== "" &&
    err.message !== "Unknown error"
  ) {
    return err.message;
  }

  return "An unexpected error occurred. Please try again.";
}
